import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { companiesApi, type Company, type UpsertCompany } from "@/lib/companies";
import { clientsApi, type ClientSummaryDto, type UpdateClientRequest } from "@/lib/clients-accounts";
import { DetailPage } from "@/archetypes/DetailPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { NewClientDialog } from "@/components/new-client-dialog";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { CompanyDatasheetsTab } from "./tabs/CompanyDatasheetsTab";

const NONE_CONTACT = "__none__";

function buildUpsertRequest(company: Company, overrides: Partial<UpsertCompany>): UpsertCompany {
  return {
    companyName: company.companyName,
    departmentName: company.departmentName,
    accountsContactClientId: company.accountsContactClientId,
    invoiceEmail: company.invoiceEmail,
    accountsCCEmailAddress: company.accountsCCEmailAddress,
    plainPackaging: company.plainPackaging,
    hasApprovedCreditAccount: company.hasApprovedCreditAccount,
    ...overrides,
  };
}

function DetailsSection({
  companyId,
  company,
  contacts,
}: {
  companyId: number;
  company: Company;
  contacts: ClientSummaryDto[];
}) {
  const qc = useQueryClient();
  const [companyName, setCompanyName] = useState(company.companyName);
  const [departmentName, setDepartmentName] = useState(company.departmentName ?? "");
  const [invoiceEmail, setInvoiceEmail] = useState(company.invoiceEmail ?? "");
  const [accountsCcEmail, setAccountsCcEmail] = useState(company.accountsCCEmailAddress ?? "");
  const [plainPackaging, setPlainPackaging] = useState(company.plainPackaging);
  const [hasApprovedCreditAccount, setHasApprovedCreditAccount] = useState(company.hasApprovedCreditAccount);
  const [accountsContactClientId, setAccountsContactClientId] = useState(company.accountsContactClientId);

  // Sync local state if company data refreshes (e.g. after another edit elsewhere)
  useEffect(() => {
    setCompanyName(company.companyName);
    setDepartmentName(company.departmentName ?? "");
    setInvoiceEmail(company.invoiceEmail ?? "");
    setAccountsCcEmail(company.accountsCCEmailAddress ?? "");
    setPlainPackaging(company.plainPackaging);
    setHasApprovedCreditAccount(company.hasApprovedCreditAccount);
    setAccountsContactClientId(company.accountsContactClientId);
  }, [company]);

  const patchMut = useMutation({
    mutationFn: (body: UpsertCompany) => companiesApi.patch(companyId, body),
    onSuccess: () => {
      toast.success("Saved");
      void qc.invalidateQueries({ queryKey: ["company", companyId] });
    },
    onError: () => {
      toast.error("Failed to save company");
    },
  });

  function handleSave() {
    patchMut.mutate(
      buildUpsertRequest(company, {
        companyName,
        departmentName: departmentName || null,
        invoiceEmail: invoiceEmail || null,
        accountsCCEmailAddress: accountsCcEmail || null,
        plainPackaging,
        hasApprovedCreditAccount,
        accountsContactClientId,
      }),
    );
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 font-medium">Company details</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="cp-name">Company name *</Label>
          <Input id="cp-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="cp-dept">Department</Label>
          <Input id="cp-dept" value={departmentName} onChange={(e) => setDepartmentName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cp-email">Invoice email</Label>
          <Input id="cp-email" type="email" value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cp-accounts-cc">Accounts CC email</Label>
          <Input
            id="cp-accounts-cc"
            value={accountsCcEmail}
            onChange={(e) => setAccountsCcEmail(e.target.value)}
            placeholder="Cc'd on statement emails"
          />
        </div>
        <div>
          <Label htmlFor="cp-accounts-contact">Accounts contact</Label>
          <Select
            value={accountsContactClientId !== null ? String(accountsContactClientId) : NONE_CONTACT}
            onValueChange={(v) => setAccountsContactClientId(v === NONE_CONTACT ? null : Number(v))}
            disabled={patchMut.isPending}
          >
            <SelectTrigger id="cp-accounts-contact">
              <SelectValue placeholder="Select contact" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_CONTACT}>— none —</SelectItem>
              {contacts.map((c) => (
                <SelectItem key={c.clientId} value={String(c.clientId)}>
                  {c.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        <label htmlFor="cp-plain-pkg" className="flex items-center gap-2 text-sm">
          <Switch id="cp-plain-pkg" checked={plainPackaging} onCheckedChange={setPlainPackaging} disabled={patchMut.isPending} />
          Plain packaging
        </label>
        <label htmlFor="cp-credit" className="flex items-center gap-2 text-sm">
          <Switch id="cp-credit" checked={hasApprovedCreditAccount} onCheckedChange={setHasApprovedCreditAccount} disabled={patchMut.isPending} />
          Approved credit account
        </label>
      </div>
      <div className="mt-4">
        <Button onClick={handleSave} disabled={patchMut.isPending || companyName.trim().length === 0}>
          Save
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Contacts section
// ---------------------------------------------------------------------------

function ContactRow({
  contact,
  companyId,
  company,
  isAccountsContact,
}: {
  contact: ClientSummaryDto;
  companyId: number;
  company: Company;
  isAccountsContact: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const setAccountsContactMut = useMutation({
    mutationFn: () =>
      companiesApi.patch(companyId, buildUpsertRequest(company, { accountsContactClientId: contact.clientId })),
    onSuccess: () => {
      toast.success("Accounts contact updated");
      void qc.invalidateQueries({ queryKey: ["company", companyId] });
    },
    onError: () => toast.error("Failed to update accounts contact"),
  });

  const removeMut = useMutation({
    mutationFn: async () => {
      const client = await clientsApi.get(contact.clientId);
      const body: UpdateClientRequest = {
        firstName: client.firstName,
        lastName: client.lastName,
        emailAddress: client.emailAddress,
        ccEmailAddress: client.ccEmailAddress,
        phone: client.phone,
        fax: client.fax,
        mobilePhone: client.mobilePhone,
        companyId: null,
        companyName: client.companyName,
        departmentName: client.departmentName,
        branch: client.branch,
        lockCompanyName: client.lockCompanyName,
        showEmail: client.showEmail,
        plainPackaging: client.plainPackaging,
        summary: client.summary,
        deliveryAddressId: client.deliveryAddressId,
        invoiceAddressId: client.invoiceAddressId,
        hasApprovedCreditAccount: client.hasApprovedCreditAccount,
        hasWebSiteLogin: client.hasWebSiteLogin,
      };
      return clientsApi.patch(contact.clientId, body);
    },
    onSuccess: () => {
      toast.success("Contact removed from company");
      void qc.invalidateQueries({ queryKey: ["company-clients", companyId] });
      // The backend clears a dangling accounts-contact pointer when its client is
      // unlinked, so re-fetch the company details too (picker/badge must reflect it).
      void qc.invalidateQueries({ queryKey: ["company", companyId] });
    },
    onError: () => toast.error("Failed to remove contact"),
  });

  async function handleRemove() {
    const ok = await confirm({
      title: "Remove from company?",
      body: `Unlink ${contact.fullName} from this company? Their client record is kept.`,
      confirmLabel: "Remove",
      intent: "destructive",
    });
    if (!ok) return;
    removeMut.mutate();
  }

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          {contact.fullName}
          {isAccountsContact && <Badge variant="info">Accounts contact</Badge>}
        </div>
      </TableCell>
      <TableCell>{contact.emailAddress ?? "—"}</TableCell>
      <TableCell>{contact.phone ?? "—"}</TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/clients/${contact.clientId}`)}>
            Open
          </Button>
          {!isAccountsContact && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAccountsContactMut.mutate()}
              disabled={setAccountsContactMut.isPending}
            >
              Set as accounts contact
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemove}
            disabled={removeMut.isPending}
          >
            Remove from company
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ContactsSection({ companyId, company }: { companyId: number; company: Company }) {
  const [addOpen, setAddOpen] = useState(false);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["company-clients", companyId],
    queryFn: () => clientsApi.list(`companyId=${companyId}&pageSize=200`),
  });

  const contacts = data?.items ?? [];

  return (
    <section className="mt-4 rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-medium">Contacts</h3>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          Add contact
        </Button>
      </div>
      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No contacts for this company yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((c) => (
              <ContactRow
                key={c.clientId}
                contact={c}
                companyId={companyId}
                company={company}
                isAccountsContact={c.clientId === company.accountsContactClientId}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <NewClientDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultCompanyId={companyId}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ["company-clients", companyId] });
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Datasheets section — screen D-2 (design spec §9): the standing per-customer
// order profiles for this company, scoped here rather than given their own route.
// ---------------------------------------------------------------------------

function DatasheetsSection({ companyId }: { companyId: number }) {
  return (
    <section className="mt-4 rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 font-medium">Datasheets</h3>
      <CompanyDatasheetsTab companyId={companyId} />
    </section>
  );
}

export function CompanyProfilePage() {
  const { id = "0" } = useParams();
  const companyId = Number(id);
  const { has } = usePermissions();

  const { data: company } = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => companiesApi.get(companyId),
  });

  const { data: contactsResult } = useQuery({
    queryKey: ["company-clients", companyId],
    queryFn: () => clientsApi.list(`companyId=${companyId}&pageSize=200`),
    enabled: !!company,
  });

  return (
    <DetailPage title={company ? company.companyName : "Loading…"}>
      {company ? (
        <>
          <DetailsSection companyId={companyId} company={company} contacts={contactsResult?.items ?? []} />
          <ContactsSection companyId={companyId} company={company} />
          {has("datasheet:read") && <DatasheetsSection companyId={companyId} />}
        </>
      ) : null}
    </DetailPage>
  );
}
